import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import FooterCopyright from '../components/FooterCopyright'

// Google Sign-In SDK types
interface GoogleTokenResponse {
  access_token?: string
  error?: string
}

interface GoogleTokenClientConfig {
  client_id: string
  scope: string
  callback: (response: GoogleTokenResponse) => void
  error_callback?: (error: { type: string }) => void
}

interface GoogleTokenClient {
  requestAccessToken: () => void
}

interface GooglePromptNotification {
  isNotDisplayed: () => boolean
}

interface GoogleAccountsId {
  initialize: (config: { client_id: string; callback: (response: { credential: string }) => void }) => void
  prompt: (callback: (notification: GooglePromptNotification) => void) => void
}

interface GoogleAccountsOAuth2 {
  initTokenClient: (config: GoogleTokenClientConfig) => GoogleTokenClient
}

interface GoogleAccounts {
  id?: GoogleAccountsId
  oauth2?: GoogleAccountsOAuth2
}

interface GoogleSDK {
  accounts?: GoogleAccounts
}

// Apple Sign-In SDK types
interface AppleSignInResponse {
  authorization?: {
    id_token?: string
  }
  user?: {
    name?: {
      firstName?: string
      lastName?: string
    }
  }
}

interface AppleAuth {
  init: (config: {
    clientId: string
    scope: string
    redirectURI: string
    usePopup: boolean
  }) => void
  signIn: () => Promise<AppleSignInResponse>
}

interface AppleIDSDK {
  auth?: AppleAuth
}

declare global {
  interface Window {
    google?: GoogleSDK
    AppleID?: AppleIDSDK
  }
}

// Backend error response type
interface BackendError {
  response?: {
    data?: {
      message?: string
      error?: string
    }
  }
  message?: string
}

type LoginUIState = 'idle' | 'authenticating' | 'error'

const LoginPage: React.FC = () => {
  const { login } = useAuth()

  const [uiState, setUiState] = useState<LoginUIState>('idle')
  const [error, setError] = useState<string | null>(null)

  const isAuthenticating = uiState === 'authenticating'

  const attemptLogin = useCallback(async (
    provider: 'google' | 'apple',
    token: string,
    fullName?: { givenName?: string; familyName?: string }
  ) => {
    setUiState('authenticating')
    setError(null)
    try {
      await login({ provider, token, fullName })
      // useAuth onSuccess handles navigation to /dashboard
    } catch (err) {
      const backendError = err as BackendError
      setError(
        backendError.response?.data?.error ||
        backendError.response?.data?.message ||
        backendError.message ||
        'Authentication failed'
      )
      setUiState('error')
    }
  }, [login])

  const handleGoogleCallback = useCallback(async (response: { credential: string }) => {
    await attemptLogin('google', response.credential)
  }, [attemptLogin])

  const handleGoogleLogin = () => {
    setError(null)

    if (window.google?.accounts?.oauth2) {
      try {
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          scope: 'openid email profile',
          callback: (response: GoogleTokenResponse) => {
            if (response.access_token) {
              attemptLogin('google', response.access_token)
            } else if (response.error) {
              if (response.error !== 'popup_closed_by_user') {
                setError('Google authentication failed')
                setUiState('error')
              }
            }
          },
          error_callback: (error: { type: string }) => {
            if (error.type !== 'popup_closed_by_user') {
              setError('Google authentication failed')
              setUiState('error')
            }
          }
        })
        client.requestAccessToken()
      } catch {
        setError('Failed to initialize Google authentication')
        setUiState('error')
      }
    } else if (window.google?.accounts?.id) {
      try {
        window.google.accounts.id.prompt((notification: GooglePromptNotification) => {
          if (notification.isNotDisplayed()) {
            setError('Google sign-in popup blocked or not available')
            setUiState('error')
          }
        })
      } catch {
        setError('Google authentication failed')
        setUiState('error')
      }
    } else {
      setError('Google authentication not available')
      setUiState('error')
    }
  }

  useEffect(() => {
    const initializeGoogle = () => {
      if (window.google?.accounts?.id) {
        try {
          window.google.accounts.id.initialize({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
            callback: handleGoogleCallback
          })
        } catch {
          setError('Failed to initialize Google authentication')
        }
      } else {
        setTimeout(initializeGoogle, 1000)
      }
    }

    initializeGoogle()
  }, [handleGoogleCallback])

  const handleAppleLogin = async () => {
    setError(null)

    if (!window.AppleID?.auth) {
      setError('Apple Sign-In not available')
      setUiState('error')
      return
    }

    try {
      window.AppleID.auth.init({
        clientId: import.meta.env.VITE_APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: `${window.location.origin}/auth/apple/callback`,
        usePopup: true,
      })

      const response = await window.AppleID.auth.signIn()

      if (response.authorization?.id_token) {
        const fullName = response.user?.name ? {
          givenName: response.user.name.firstName,
          familyName: response.user.name.lastName,
        } : undefined

        await attemptLogin('apple', response.authorization.id_token, fullName)
      } else {
        setError('Failed to get authorization from Apple')
        setUiState('error')
      }
    } catch (err) {
      const appleError = err as { error?: string }
      if (appleError.error !== 'popup_closed_by_user') {
        setError(appleError.error || 'Apple authentication failed')
        setUiState('error')
      }
    }
  }

  const authButtonClasses =
    'w-full flex items-center justify-center gap-3 px-6 py-4 min-h-[48px] bg-white border border-paper-200 rounded-md text-ink-900 text-sm font-medium cursor-pointer transition-colors duration-150 hover:bg-paper-50 hover:border-ink-300 disabled:opacity-50 disabled:cursor-not-allowed'

  return (
    <div className="min-h-[100dvh] grid grid-cols-1 md:grid-cols-[1.1fr_1fr] bg-paper-0">

      {/* ── Left: the brand panel (desktop) ─────────────────────── */}
      <div className="hidden md:flex relative overflow-hidden bg-brand-950 flex-col justify-between p-12">
        {/* Concentric seal rings — the mark of the verdict */}
        <div className="absolute -right-40 -bottom-40 pointer-events-none" aria-hidden="true">
          <div className="w-[560px] h-[560px] rounded-full border border-white/[0.06] flex items-center justify-center">
            <div className="w-[420px] h-[420px] rounded-full border border-white/[0.08] flex items-center justify-center">
              <div className="w-[280px] h-[280px] rounded-full border border-accent-300/20" />
            </div>
          </div>
        </div>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(circle at 30% 25%, rgb(var(--tr-accent-500) / 0.08) 0%, transparent 55%)' }}
        />

        <img src="/assets/logo-white.png" alt="Treu" className="relative h-8 w-auto self-start" />

        <div className="relative max-w-[400px]">
          <p className="font-display text-xs font-medium uppercase tracking-[0.25em] text-accent-300 mb-5">
            The instrument of discernment
          </p>
          <h1 className="font-display text-4xl font-bold text-white leading-tight mb-5">
            Truth, verified.
          </h1>
          <p className="text-white/70 leading-relaxed">
            Every claim you check resolves into a sealed verdict — scored,
            reasoned, and sourced. Be the one your network trusts.
          </p>
        </div>

        <FooterCopyright variant="compact" className="relative text-white/40 text-left" />
      </div>

      {/* ── Right: the auth panel ───────────────────────────────── */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-[380px]">
          {/* Mobile brand header */}
          <div className="md:hidden text-center mb-10">
            <img src="/assets/logo-dark.png" alt="Treu" className="w-20 h-20 mx-auto mb-2" />
            <p className="font-display text-xs font-medium uppercase tracking-[0.25em] text-accent-600">
              Truth, verified
            </p>
          </div>

          {/* OAuth Buttons */}
          <div>
            <h2 className="font-display text-2xl font-bold text-ink-900 mb-2">Welcome.</h2>
            <p className="text-ink-600 text-sm mb-8">
              Sign in to start verifying claims.
            </p>

            {error && (
              <div className="mb-6 p-4 bg-danger-100 border border-danger-500/25 rounded-md text-sm text-ink-900">
                {error}
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={handleGoogleLogin}
                disabled={isAuthenticating}
                className={authButtonClasses}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {isAuthenticating ? 'Authenticating...' : 'Login with Google'}
              </button>

              <button
                onClick={handleAppleLogin}
                disabled={isAuthenticating}
                className={authButtonClasses}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                {isAuthenticating ? 'Authenticating...' : 'Login with Apple'}
              </button>
            </div>
          </div>

          {/* Terms Footer */}
          <div className="text-center mt-10 text-ink-400 text-xs leading-relaxed">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="text-inherit underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-inherit underline">Privacy Policy</Link>
          </div>

          <div className="text-center mt-4">
            <Link to="/" className="text-ink-400 text-[0.8rem] no-underline hover:text-ink-600 transition-colors duration-150">
              ← Back to home
            </Link>
          </div>

          <FooterCopyright variant="compact" className="mt-6 text-ink-300 md:hidden" />
        </div>
      </div>
    </div>
  )
}

export default LoginPage
